// Drain a PostgREST read in bounded pages instead of a single huge `.range()`
// "cap-lift". A lone `.range(0, 49_999)` does NOT reliably beat PostgREST's
// per-response row cap — it just *looks* like it asks for 50k; paging is the
// only correct way to pull a full high-cardinality table (snapshots, POPIA
// "give me all my data" exports). This is the sanctioned alternative the
// `dal:range-cap-lift` gate points at (scripts/dal/check-cap-lift.mjs).
//
// Pass a factory that builds a FRESH query for each page's inclusive [from,to]
// window — PostgREST builders are single-use, so the range must be applied per
// page. Returns the accumulated rows plus the first error encountered; rows
// pulled before an error are still returned, so a mid-drain failure degrades to
// partial data rather than nothing (the caller decides whether to keep it).

export interface PageResult<T> {
  data: T[] | null
  error: { message: string } | null
}

export async function fetchAllPaged<T>(
  makePage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  opts: { pageSize?: number; maxRows?: number } = {},
): Promise<{ rows: T[]; error: string | null }> {
  const pageSize = Math.max(1, opts.pageSize ?? 1000)
  const maxRows = opts.maxRows ?? Number.POSITIVE_INFINITY
  const rows: T[] = []
  for (let from = 0; from < maxRows; from += pageSize) {
    const to = Math.min(from + pageSize, maxRows) - 1
    const { data, error } = await makePage(from, to)
    if (error) return { rows, error: error.message }
    const batch = data ?? []
    rows.push(...batch)
    if (batch.length < pageSize) break   // short (or empty) page = table drained
  }
  return { rows, error: null }
}
